import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo, CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CampoDoMotivo } from '@/componentes/CampoDoMotivo';
import { ControleCarregando } from '@/componentes/Esqueleto';
import { Janela, Sobreposicao } from '@/componentes/JanelaDeAcao';
import { formatarCompetencia, formatarDia, formatarReais, interpretarNumero } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';

import { apiDasIntegracoes, lerArquivo, novaChave, useRegistros } from './api';
import {
  CADASTROS_DE_ENTRADA,
  ENTIDADES_DE_IMPORTACAO,
  ENTIDADES_DE_SAIDA,
  RESOLUCOES,
  ROTULO_DA_SAIDA,
  ROTULO_DA_ENTIDADE,
  ROTULO_DA_RESOLUCAO,
  ROTULO_DO_FORMATO,
  ROTULO_DO_TIPO_DE_DIVERGENCIA,
  ROTULO_DO_TIPO_DE_SISTEMA,
  TAMANHO_MAXIMO,
  alvoDaDivergencia,
  competenciasFechadas,
  diferencaEscrita,
  retornoDaCorrespondencia,
  retornoDaDecisao,
  retornoDaImportacao,
  retornoDaProposta,
  retornoDaResolucao,
  retornoDaSaida,
  retornoDaSaidaDeRegistro,
  retornoDaTolerancia,
  retornoDaTroca,
  retornoDoDescarte,
  retornoDoEncerramento,
  retornoDoRetorno,
  retornoDoSistema,
  tituloDaSaida,
  tituloDaSaidaDeRegistro,
  tituloDoRetorno,
  vigenciaEscrita,
  type CadastroDeEntrada,
  type Correspondencia,
  type DescricaoDaEntidade,
  type Divergencia,
  type Entidade,
  type EntidadeDeImportacao,
  type EntidadeDeSaida,
  type Formato,
  type LinhaDaMatriz,
  type Lote,
  type RespostaDaImportacao,
  type RespostaDaSaida,
  type RespostaDaSaidaDeRegistro,
  type RespostaDoRetorno,
  type SistemaExterno,
} from './integracoes';

/**
 * As janelas das integrações na mesa (p.30): tarefa curta, ao centro, no máximo
 * seis campos; a proposta da matriz, com sete, vai na gaveta (p.29). Cada uma é
 * desenhada só por quem tem a licença da gravação dela (a página decide), e
 * cada gravação declara o seu retorno (R04).
 */

function Recusa({ erro }: { readonly erro: unknown }) {
  const f = explicarFalha(erro);
  return (
    <div role="alert" className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-3">
      <span className="txt-corpo text-tinta">{f.erro}</span>
      <span className="txt-apoio text-tinta-secundaria">{f.comoResolver}</span>
    </div>
  );
}

function useInvalidar() {
  const cliente = useQueryClient();
  return () => {
    void cliente.invalidateQueries({ queryKey: ['integracoes'] });
  };
}

/* ========================================================== o sistema */

export function JanelaDeSistema({ aberta, aoFechar }: { readonly aberta: boolean; readonly aoFechar: () => void }) {
  const invalidar = useInvalidar();
  const [codigo, setCodigo] = useState('');
  const [nome, setNome] = useState('');
  const [tipo, setTipo] = useState('erp');
  const [observacao, setObservacao] = useState('');
  const cadastrar = useMutation({
    mutationFn: (d: { codigo: string; nome: string; tipo: string; observacao?: string }) => apiDasIntegracoes.cadastrarSistema(d),
    meta: { retorno: retornoDe<{ codigo: string; nome: string; tipo: string }>((r) => ({ titulo: 'Sistema externo cadastrado', ...retornoDoSistema(r) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    cadastrar.reset();
    setCodigo('');
    setNome('');
    setTipo('erp');
    setObservacao('');
    aoFechar();
  }
  const falta = codigo.trim().length < 2 || nome.trim().length < 3;
  return (
    <Janela
      titulo="Cadastrar sistema externo"
      descricao="O sistema de fora com que a empresa troca dados. Ele não é nomeado no código: é dado da empresa."
      aberta={aberta}
      aoFechar={fechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>Cancelar</Botao>
          <Botao
            nivel="primario"
            disabled={cadastrar.isPending || falta}
            onClick={() => {
              cadastrar.mutate({ codigo: codigo.trim(), nome: nome.trim(), tipo, ...(observacao.trim() ? { observacao: observacao.trim() } : {}) });
            }}
          >
            {cadastrar.isPending ? 'Cadastrando…' : 'Cadastrar sistema'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {cadastrar.isError ? <Recusa erro={cadastrar.error} /> : null}
        <CampoTexto rotulo="Código" valor={codigo} aoMudar={setCodigo} placeholder="ERP" maxLength={30} ajuda="Curto, para o arquivo e para a tela: de 2 a 30 caracteres." />
        <CampoTexto rotulo="Nome" valor={nome} aoMudar={setNome} placeholder="ERP da matriz" maxLength={80} />
        <CampoSelecao rotulo="Tipo" valor={tipo} aoMudar={setTipo} opcoes={Object.entries(ROTULO_DO_TIPO_DE_SISTEMA).map(([valor, rotulo]) => ({ valor, rotulo }))} />
        <CampoTexto rotulo="Observação" opcional valor={observacao} aoMudar={setObservacao} placeholder="Versão, quem responde por ele" maxLength={400} />
      </div>
    </Janela>
  );
}

/* ====================================================== a proposta */

export function GavetaDaProposta({
  aberta,
  aoFechar,
  entidades,
  sistemas,
  hoje,
}: {
  readonly aberta: boolean;
  readonly aoFechar: () => void;
  readonly entidades: readonly DescricaoDaEntidade[];
  readonly sistemas: readonly SistemaExterno[];
  readonly hoje: string;
}) {
  const base = useId();
  const invalidar = useInvalidar();
  const [entidade, setEntidade] = useState<Entidade>('fornecedor');
  const [mestre, setMestre] = useState('');
  const [troca, setTroca] = useState('');
  const [formato, setFormato] = useState<Formato>('arquivo_csv');
  const [frequencia, setFrequencia] = useState('7');
  const [inicio, setInicio] = useState(hoje);
  const [contingencia, setContingencia] = useState('');
  const ativos = sistemas.filter((s) => s.ativo);
  const descricao = entidades.find((e) => e.entidade === entidade);
  const fiscal = descricao?.fiscal ?? false;
  /* Mestre de fora: a troca e com ele, ou nenhuma. Plataforma: qualquer sistema, ou nenhuma. */
  const opcoesDaTroca = mestre ? ativos.filter((s) => s.id === mestre) : ativos;
  const propor = useMutation({
    mutationFn: (d: Parameters<typeof apiDasIntegracoes.proporLinha>[0]) => apiDasIntegracoes.proporLinha(d),
    meta: { retorno: retornoDe<{ entidade: Entidade; direcao: 'entrada' | 'saida' | 'nenhuma'; inicio: string }>((r) => ({ titulo: 'Linha da matriz proposta', ...retornoDaProposta(r) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    propor.reset();
    setEntidade('fornecedor');
    setMestre('');
    setTroca('');
    setFormato('arquivo_csv');
    setFrequencia('7');
    setInicio(hoje);
    setContingencia('');
    aoFechar();
  }
  const dias = interpretarNumero(frequencia);
  const falta = contingencia.trim().length < 15 || dias === null || !inicio || (fiscal && !mestre);
  return (
    <Sobreposicao
      lugar="direita"
      titulo="Propor linha da matriz"
      descricao="Quem é o mestre da entidade, com quem a troca acontece e o que fazer se ela falhar. Outra pessoa aprova."
      aberta={aberta}
      aoFechar={fechar}
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>Cancelar</Botao>
          <Botao
            nivel="primario"
            disabled={propor.isPending || falta}
            onClick={() => {
              if (dias === null) return;
              propor.mutate({
                entidade, sistemaMestreId: mestre || null, sistemaParceiroId: troca || null, formato,
                frequenciaDias: Math.round(dias), contingencia: contingencia.trim(), inicio,
              });
            }}
          >
            {propor.isPending ? 'Propondo…' : 'Propor linha'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {propor.isError ? <Recusa erro={propor.error} /> : null}
        <CampoSelecao
          rotulo="Entidade"
          valor={entidade}
          aoMudar={(v) => {
            setEntidade(v as Entidade);
          }}
          opcoes={entidades.map((e) => ({ valor: e.entidade, rotulo: `${ROTULO_DA_ENTIDADE[e.entidade]}${e.fiscal ? ' (fiscal)' : ''}` }))}
        />
        {descricao ? <p className="txt-apoio text-pretty text-tinta-secundaria">{descricao.situacao}</p> : null}
        <CampoSelecao
          rotulo="Sistema mestre"
          valor={mestre}
          aoMudar={(v) => {
            setMestre(v);
            setTroca(v);
          }}
          rotuloVazio={fiscal ? 'Escolha o sistema de fora' : 'A plataforma'}
          opcoes={ativos.map((s) => ({ valor: s.id, rotulo: s.nome }))}
        />
        {fiscal ? <p className="txt-apoio text-pretty text-atencao-texto">O fiscal nunca é da plataforma: escolha o sistema de fora que é o mestre.</p> : null}
        <CampoSelecao
          rotulo="Troca com"
          valor={troca}
          aoMudar={setTroca}
          rotuloVazio="Nenhuma troca (só documenta o mestre)"
          opcoes={opcoesDaTroca.map((s) => ({ valor: s.id, rotulo: s.nome }))}
        />
        <div className="grid grid-cols-2 gap-4">
          <CampoSelecao rotulo="Formato" valor={formato} aoMudar={(v) => { setFormato(v as Formato); }} opcoes={(Object.keys(ROTULO_DO_FORMATO) as Formato[]).map((f) => ({ valor: f, rotulo: ROTULO_DO_FORMATO[f] }))} />
          <CampoTexto rotulo="A cada (dias)" valor={frequencia} aoMudar={setFrequencia} placeholder="7" maxLength={3} />
        </div>
        <Campo id={`${base}-inicio`} rotulo="Vale a partir de">
          <input id={`${base}-inicio`} type="date" className="controle w-full" value={inicio} onChange={(e) => { setInicio(e.target.value); }} />
        </Campo>
        <CampoDoMotivo
          rotulo="Contingência"
          valor={contingencia}
          aoMudar={setContingencia}
          minimo={15}
          maximo={600}
          exemplo="Se o arquivo não vier, importar o CSV exportado do ERP pela tela"
        />
      </div>
    </Sobreposicao>
  );
}

/* ====================================================== a decisao */

export function JanelaDeDecisao({ alvo, aoFechar }: { readonly alvo: LinhaDaMatriz | null; readonly aoFechar: () => void }) {
  const invalidar = useInvalidar();
  const [nota, setNota] = useState('');
  const decidir = useMutation({
    mutationFn: (v: { id: string; decisao: 'aprovada' | 'recusada'; nota: string }) => apiDasIntegracoes.decidirLinha(v.id, v.decisao, v.nota),
    meta: {
      retorno: retornoDe<{ entidade: Entidade; decisao: 'aprovada' | 'recusada'; inicio: string }, { nota: string }>((r, v) => ({
        titulo: r.decisao === 'aprovada' ? 'Linha da matriz aprovada' : 'Linha da matriz recusada',
        ...retornoDaDecisao(r, v.nota),
      })),
      silenciarFalha: true,
    },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    decidir.reset();
    setNota('');
    aoFechar();
  }
  const curta = nota.trim().length < 15;
  return (
    <Janela
      titulo={alvo ? `Decidir a linha de ${ROTULO_DA_ENTIDADE[alvo.entidade].toLowerCase()}` : 'Decidir a linha'}
      descricao={alvo ? `Proposta por ${alvo.propostaPor ?? '—'} em ${formatarDia(alvo.propostaEm)}. Aprovada, ela vale ${vigenciaEscrita(alvo)} e não muda: a troca de sistema é encerrar e aprovar outra.` : ''}
      aberta={alvo !== null}
      aoFechar={fechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>Voltar</Botao>
          <Botao nivel="destrutivo" disabled={decidir.isPending || curta} onClick={() => { if (alvo) decidir.mutate({ id: alvo.id, decisao: 'recusada', nota: nota.trim() }); }}>
            Recusar linha
          </Botao>
          <Botao nivel="primario" disabled={decidir.isPending || curta} onClick={() => { if (alvo) decidir.mutate({ id: alvo.id, decisao: 'aprovada', nota: nota.trim() }); }}>
            {decidir.isPending ? 'Gravando…' : 'Aprovar linha'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {decidir.isError ? <Recusa erro={decidir.error} /> : null}
        {alvo ? (
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2">
            <dt className="txt-apoio text-tinta-secundaria">Mestre</dt>
            <dd className="txt-corpo text-tinta">{alvo.sistemaMestre ?? 'A plataforma'}</dd>
            <dt className="txt-apoio text-tinta-secundaria">Troca</dt>
            <dd className="txt-corpo text-tinta">{alvo.sistemaParceiro ? `${alvo.sistemaParceiro} · ${ROTULO_DO_FORMATO[alvo.formato]} a cada ${String(alvo.frequenciaDias)} dias` : 'Nenhuma'}</dd>
            <dt className="txt-apoio text-tinta-secundaria">Contingência</dt>
            <dd className="txt-corpo text-pretty text-tinta">{alvo.contingencia}</dd>
          </dl>
        ) : null}
        <CampoDoMotivo rotulo="Nota da decisão" valor={nota} aoMudar={setNota} minimo={15} maximo={400} exemplo="Aprovado com a contabilidade na reunião de 07/10/2026" />
      </div>
    </Janela>
  );
}

/* =================================================== o encerramento */

export function JanelaDeEncerramento({ alvo, aoFechar, hoje }: { readonly alvo: LinhaDaMatriz | null; readonly aoFechar: () => void; readonly hoje: string }) {
  const base = useId();
  const invalidar = useInvalidar();
  const [ultimo, setUltimo] = useState(hoje);
  const [motivo, setMotivo] = useState('');
  const encerrar = useMutation({
    mutationFn: (v: { id: string; ultimoDia: string; motivo: string }) => apiDasIntegracoes.encerrarLinha(v.id, v.ultimoDia, v.motivo),
    meta: { retorno: retornoDe<{ entidade: Entidade; ultimoDia: string }, { motivo: string }>((r, v) => ({ titulo: 'Vigência encerrada', ...retornoDoEncerramento(r, v.motivo) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    encerrar.reset();
    setUltimo(hoje);
    setMotivo('');
    aoFechar();
  }
  return (
    <Janela
      titulo={alvo ? `Encerrar a linha de ${ROTULO_DA_ENTIDADE[alvo.entidade].toLowerCase()}?` : 'Encerrar a linha?'}
      descricao="Depois do último dia, a entidade fica sem mestre até outra linha ser aprovada: é o plano de transição. A linha encerrada continua valendo para os dias dela."
      aberta={alvo !== null}
      aoFechar={fechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>Manter a linha</Botao>
          <Botao nivel="destrutivo" disabled={encerrar.isPending || motivo.trim().length < 15 || !ultimo} onClick={() => { if (alvo) encerrar.mutate({ id: alvo.id, ultimoDia: ultimo, motivo: motivo.trim() }); }}>
            {encerrar.isPending ? 'Encerrando…' : 'Encerrar vigência'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {encerrar.isError ? <Recusa erro={encerrar.error} /> : null}
        <Campo id={`${base}-ultimo`} rotulo="Último dia da vigência">
          <input id={`${base}-ultimo`} type="date" className="controle w-full" value={ultimo} onChange={(e) => { setUltimo(e.target.value); }} />
        </Campo>
        <CampoDoMotivo valor={motivo} aoMudar={setMotivo} minimo={15} maximo={400} exemplo="Troca do ERP a partir de 01/11/2026" />
      </div>
    </Janela>
  );
}

/* ====================================================== a importacao */

export function JanelaDeImportacao({
  aberta,
  aoFechar,
  aoImportar,
  mestres,
}: {
  readonly aberta: boolean;
  readonly aoFechar: () => void;
  readonly aoImportar: (loteId: string) => void;
  /** O mestre de hoje de cada entidade com troca de entrada (ausente: sem linha aprovada). */
  readonly mestres: Readonly<Partial<Record<Entidade, string>>>;
}) {
  const base = useId();
  const invalidar = useInvalidar();
  const [entidade, setEntidade] = useState<EntidadeDeImportacao>('fornecedor');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [problema, setProblema] = useState<string | null>(null);
  const importar = useMutation({
    mutationFn: async (v: { entidade: EntidadeDeImportacao; arquivo: File; chave: string }) =>
      apiDasIntegracoes.importar({ entidade: v.entidade, nomeArquivo: v.arquivo.name, conteudo: await lerArquivo(v.arquivo) }, v.chave),
    meta: {
      retorno: retornoDe<RespostaDaImportacao, { arquivo: File }>((r, v) => ({
        titulo: r.repetido ? 'Lote já importado' : 'Arquivo importado para conferência',
        ...retornoDaImportacao(r, v.arquivo.name),
      })),
      silenciarFalha: true,
    },
    onSuccess: (r) => {
      invalidar();
      fechar();
      aoImportar(r.loteId);
    },
  });
  function fechar() {
    importar.reset();
    setArquivo(null);
    setProblema(null);
    aoFechar();
  }
  const mestre = mestres[entidade] ?? null;
  return (
    <Janela
      titulo="Importar arquivo"
      descricao="O arquivo do sistema mestre, no formato v1 (CSV ou JSON). As linhas ficam em conferência: nada vale antes de você promover o lote."
      aberta={aberta}
      aoFechar={fechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>Cancelar</Botao>
          <Botao
            nivel="primario"
            disabled={importar.isPending || !arquivo || !mestre}
            onClick={() => {
              if (arquivo) importar.mutate({ entidade, arquivo, chave: novaChave() });
            }}
          >
            {importar.isPending ? 'Importando…' : 'Importar arquivo'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {importar.isError ? <Recusa erro={importar.error} /> : null}
        <CampoSelecao rotulo="Entidade" valor={entidade} aoMudar={(v) => { setEntidade(v as EntidadeDeImportacao); }} opcoes={ENTIDADES_DE_IMPORTACAO.map((e) => ({ valor: e, rotulo: ROTULO_DA_ENTIDADE[e] }))} />
        <p className={`txt-apoio text-pretty ${mestre ? 'text-tinta-secundaria' : 'text-atencao-texto'}`}>
          {mestre
            ? `O mestre de ${ROTULO_DA_ENTIDADE[entidade].toLowerCase()} é ${mestre}: só entra o que vem dele.`
            : `Não há sistema mestre aprovado para ${ROTULO_DA_ENTIDADE[entidade].toLowerCase()} com troca de entrada. Aprove a linha da matriz antes de importar.`}
        </p>
        <Campo id={`${base}-arquivo`} rotulo="Arquivo" ajuda="CSV com ponto e vírgula ou vírgula, ou JSON do contrato v1. Até 8 MB (20.000 linhas)." erro={problema}>
          <input
            id={`${base}-arquivo`}
            type="file"
            accept=".csv,.txt,.json,text/csv,text/plain,application/json"
            className="controle w-full"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setProblema(f && f.size > TAMANHO_MAXIMO ? 'O arquivo passa de 8 MB. Divida em partes e importe uma de cada vez.' : null);
              setArquivo(f && f.size <= TAMANHO_MAXIMO ? f : null);
            }}
          />
        </Campo>
      </div>
    </Janela>
  );
}

/* ======================================================== a saida */

export function JanelaDaSaida({
  aberta,
  aoFechar,
  aoGerar,
  hoje,
  destinos,
}: {
  readonly aberta: boolean;
  readonly aoFechar: () => void;
  readonly aoGerar: (loteId: string) => void;
  readonly hoje: string;
  /** Para onde sai cada entidade (ausente: sem linha de saída aprovada). */
  readonly destinos: Readonly<Partial<Record<Entidade, string>>>;
}) {
  const invalidar = useInvalidar();
  const meses = competenciasFechadas(hoje);
  const [oQue, setOQue] = useState<EntidadeDeSaida>('lancamento_gerencial');
  const [competencia, setCompetencia] = useState(meses[0] ?? '');
  const [motivo, setMotivo] = useState('');
  const destino = destinos[oQue] ?? null;
  const doMes = oQue === 'lancamento_gerencial';
  const gerar = useMutation({
    mutationFn: (v: { competencia: string; motivo: string; chave: string }) => apiDasIntegracoes.gerarSaida(v.competencia, v.motivo.trim() || undefined, v.chave),
    meta: { retorno: retornoDe<RespostaDaSaida>((r) => ({ titulo: tituloDaSaida(r), ...retornoDaSaida(r) })), silenciarFalha: true },
    onSuccess: (r) => {
      invalidar();
      fechar();
      aoGerar(r.loteId);
    },
  });
  /* 0108: o pedido e os cadastros saem como registro — o que mudou desde a última saída. */
  const gerarRegistro = useMutation({
    mutationFn: (v: { entidade: Exclude<EntidadeDeSaida, 'lancamento_gerencial'>; chave: string }) => apiDasIntegracoes.gerarSaidaDeRegistro(v.entidade, v.chave),
    meta: { retorno: retornoDe<RespostaDaSaidaDeRegistro>((r) => ({ titulo: tituloDaSaidaDeRegistro(r), ...retornoDaSaidaDeRegistro(r) })), silenciarFalha: true },
    onSuccess: (r) => {
      invalidar();
      fechar();
      if (r.loteId) aoGerar(r.loteId);
    },
  });
  function fechar() {
    gerar.reset();
    gerarRegistro.reset();
    setMotivo('');
    aoFechar();
  }
  const pendente = gerar.isPending || gerarRegistro.isPending;
  const erro = gerar.isError ? gerar.error : gerarRegistro.isError ? gerarRegistro.error : null;
  return (
    <Janela
      titulo="Gerar saída"
      descricao={doMes
        ? 'Os lançamentos gerenciais da competência (a DRE do mês), com a chave de troca e a revisão de cada linha. O conteúdo é do financeiro; a integração só transporta.'
        : 'O que mudou desde a última saída: o registro novo na revisão 1, o alterado na seguinte, e o pedido cancelado como cancelamento.'}
      aberta={aberta}
      aoFechar={fechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>Cancelar</Botao>
          <Botao
            nivel="primario"
            disabled={pendente || !destino || (doMes && !competencia)}
            onClick={() => {
              if (oQue === 'lancamento_gerencial') gerar.mutate({ competencia, motivo, chave: novaChave() });
              else gerarRegistro.mutate({ entidade: oQue, chave: novaChave() });
            }}
          >
            {pendente ? 'Gerando…' : 'Gerar saída'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {erro ? <Recusa erro={erro} /> : null}
        <CampoSelecao rotulo="O que sai" valor={oQue} aoMudar={(v) => { setOQue(v as EntidadeDeSaida); }} opcoes={ENTIDADES_DE_SAIDA.map((e) => ({ valor: e, rotulo: ROTULO_DA_SAIDA[e] }))} />
        <p className={`txt-apoio text-pretty ${destino ? 'text-tinta-secundaria' : 'text-atencao-texto'}`}>
          {destino
            ? `A plataforma é a mestre de ${ROTULO_DA_SAIDA[oQue].toLowerCase()}, e a saída vai para ${destino}.`
            : `Não há linha aprovada da matriz com a plataforma como mestre de ${ROTULO_DA_SAIDA[oQue].toLowerCase()} e saída para um sistema. Aprove a linha antes de gerar.`}
        </p>
        {doMes ? (
          <>
            <CampoSelecao rotulo="Competência" valor={competencia} aoMudar={setCompetencia} opcoes={meses.map((m) => ({ valor: m, rotulo: formatarCompetencia(`${m}-01`) }))} />
            <CampoDoMotivo
              rotulo="Motivo · opcional"
              valor={motivo}
              aoMudar={setMotivo}
              minimo={0}
              maximo={400}
              exemplo="Só para gerar de novo um mês que já teve retorno: por que ele muda"
            />
          </>
        ) : null}
      </div>
    </Janela>
  );
}

/* ======================================================== o retorno */

export function JanelaDoRetorno({ alvo, aoFechar }: { readonly alvo: Lote | null; readonly aoFechar: () => void }) {
  const base = useId();
  const invalidar = useInvalidar();
  const [arquivo, setArquivo] = useState<File | null>(null);
  const receber = useMutation({
    mutationFn: async (v: { id: string; arquivo: File }) => apiDasIntegracoes.receberRetorno(v.id, v.arquivo.name, await lerArquivo(v.arquivo)),
    meta: { retorno: retornoDe<RespostaDoRetorno, { arquivo: File }>((r, v) => ({ titulo: tituloDoRetorno(r), ...retornoDoRetorno(r, v.arquivo.name) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    receber.reset();
    setArquivo(null);
    aoFechar();
  }
  return (
    <Janela
      titulo="Receber o retorno"
      descricao={alvo ? `O arquivo que o sistema de fora devolveu para ${alvo.nomeArquivo}: a situação de cada linha e os totais por conta contábil.` : ''}
      aberta={alvo !== null}
      aoFechar={fechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>Cancelar</Botao>
          <Botao nivel="primario" disabled={receber.isPending || !arquivo} onClick={() => { if (alvo && arquivo) receber.mutate({ id: alvo.id, arquivo }); }}>
            {receber.isPending ? 'Conciliando…' : 'Conciliar retorno'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {receber.isError ? <Recusa erro={receber.error} /> : null}
        <Campo
          id={`${base}-retorno`}
          rotulo="Arquivo de retorno"
          ajuda={alvo && alvo.entidade !== 'lancamento_gerencial'
            ? 'CSV com registro;chave_de_troca;revisao;situacao;chave_externa;valor;mensagem, ou o JSON do contrato v1. A chave_externa vira a correspondência.'
            : 'CSV com registro;chave_de_troca;revisao;situacao;competencia;conta_contabil;valor;mensagem, ou o JSON do contrato v1.'}
        >
          <input
            id={`${base}-retorno`}
            type="file"
            accept=".csv,.txt,.json,text/csv,text/plain,application/json"
            className="controle w-full"
            onChange={(e) => { setArquivo(e.target.files?.[0] ?? null); }}
          />
        </Campo>
      </div>
    </Janela>
  );
}

/* ======================================================== o descarte */

export function JanelaDeDescarte({ alvo, aoFechar }: { readonly alvo: Lote | null; readonly aoFechar: () => void }) {
  const invalidar = useInvalidar();
  const [motivo, setMotivo] = useState('');
  const saida = alvo?.direcao === 'saida';
  const descartar = useMutation({
    mutationFn: (v: { id: string; motivo: string; saida: boolean }) =>
      v.saida ? apiDasIntegracoes.descartarSaida(v.id, v.motivo) : apiDasIntegracoes.descartarEntrada(v.id, v.motivo),
    meta: { retorno: retornoDe<{ nomeArquivo: string }, { motivo: string; saida: boolean }>((r, v) => ({ titulo: 'Lote descartado', ...retornoDoDescarte(r, v.motivo, v.saida) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    descartar.reset();
    setMotivo('');
    aoFechar();
  }
  return (
    <Janela
      titulo={alvo ? `Descartar o lote ${alvo.nomeArquivo}?` : 'Descartar o lote?'}
      descricao={saida ? 'A saída sai de cena antes do retorno; o mesmo mês pode ser gerado de novo.' : 'Nenhuma linha vira cadastro. O mesmo arquivo pode ser importado de novo.'}
      aberta={alvo !== null}
      aoFechar={fechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>Manter o lote</Botao>
          <Botao nivel="destrutivo" disabled={descartar.isPending || motivo.trim().length < 10} onClick={() => { if (alvo) descartar.mutate({ id: alvo.id, motivo: motivo.trim(), saida }); }}>
            {descartar.isPending ? 'Descartando…' : 'Descartar lote'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {descartar.isError ? <Recusa erro={descartar.error} /> : null}
        <CampoDoMotivo valor={motivo} aoMudar={setMotivo} maximo={400} exemplo="Arquivo com o mês errado" />
      </div>
    </Janela>
  );
}

/* =================================================== a correspondencia */

export function EscolhaDoRegistro({ entidade, valor, aoEscolher }: { readonly entidade: CadastroDeEntrada; readonly valor: string; readonly aoEscolher: (id: string) => void }) {
  const [busca, setBusca] = useState('');
  const registros = useRegistros(entidade, busca, true);
  const lista = registros.data?.registros ?? [];
  return (
    <div className="flex flex-col gap-2">
      <CampoTexto rotulo={`Buscar ${ROTULO_DA_ENTIDADE[entidade].toLowerCase()}`} valor={busca} aoMudar={setBusca} placeholder="Código ou parte do nome" maxLength={120} />
      {registros.isPending ? (
        <ControleCarregando oQue={`Buscando ${ROTULO_DA_ENTIDADE[entidade].toLowerCase()} na plataforma`} />
      ) : (
        <CampoSelecao
          rotulo={`${ROTULO_DA_ENTIDADE[entidade]} da plataforma`}
          valor={valor}
          aoMudar={aoEscolher}
          rotuloVazio={lista.length === 0 ? 'Nenhum com essa busca' : 'Escolha na lista'}
          opcoes={lista.map((r) => ({ valor: r.id, rotulo: `${r.codigo} · ${r.nome}${r.ativo ? '' : ' (fora de uso)'}` }))}
        />
      )}
    </div>
  );
}

export function JanelaDeCorrespondencia({ aberta, aoFechar, sistemas }: { readonly aberta: boolean; readonly aoFechar: () => void; readonly sistemas: readonly SistemaExterno[] }) {
  const invalidar = useInvalidar();
  const [sistema, setSistema] = useState('');
  const [entidade, setEntidade] = useState<CadastroDeEntrada>('fornecedor');
  const [chave, setChave] = useState('');
  const [registro, setRegistro] = useState('');
  const vincular = useMutation({
    mutationFn: (d: { sistemaId: string; entidade: CadastroDeEntrada; chaveExterna: string; registroId: string }) => apiDasIntegracoes.vincular(d),
    meta: {
      retorno: retornoDe<{ chaveExterna: string; entidade: CadastroDeEntrada; registro: { codigo: string; nome: string } | null }>((r) => ({
        titulo: 'Correspondência vinculada',
        ...retornoDaCorrespondencia(r),
      })),
      silenciarFalha: true,
    },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    vincular.reset();
    setSistema('');
    setChave('');
    setRegistro('');
    aoFechar();
  }
  return (
    <Janela
      titulo="Vincular à mão"
      descricao="Quando o arquivo não acha o cadastro que já existe na plataforma, diga qual é: a chave do sistema de fora passa a apontar para ele."
      aberta={aberta}
      aoFechar={fechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>Cancelar</Botao>
          <Botao nivel="primario" disabled={vincular.isPending || !sistema || !chave.trim() || !registro} onClick={() => { vincular.mutate({ sistemaId: sistema, entidade, chaveExterna: chave.trim(), registroId: registro }); }}>
            {vincular.isPending ? 'Vinculando…' : 'Vincular cadastro'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {vincular.isError ? <Recusa erro={vincular.error} /> : null}
        <CampoSelecao rotulo="Sistema" valor={sistema} aoMudar={setSistema} rotuloVazio="Escolha o sistema" opcoes={sistemas.filter((s) => s.ativo).map((s) => ({ valor: s.id, rotulo: s.nome }))} />
        <CampoSelecao rotulo="Entidade" valor={entidade} aoMudar={(v) => { setEntidade(v as CadastroDeEntrada); setRegistro(''); }} opcoes={CADASTROS_DE_ENTRADA.map((e) => ({ valor: e, rotulo: ROTULO_DA_ENTIDADE[e] }))} />
        <CampoTexto rotulo="Chave no sistema de fora" valor={chave} aoMudar={setChave} placeholder="F-1001" maxLength={120} ajuda="Como o sistema de fora escreve: maiúscula e minúscula contam." />
        <EscolhaDoRegistro entidade={entidade} valor={registro} aoEscolher={setRegistro} />
      </div>
    </Janela>
  );
}

export function JanelaDeTroca({ alvo, aoFechar }: { readonly alvo: Correspondencia | null; readonly aoFechar: () => void }) {
  const invalidar = useInvalidar();
  const [registro, setRegistro] = useState('');
  const [motivo, setMotivo] = useState('');
  const trocar = useMutation({
    mutationFn: (v: { id: string; registroId: string; motivo: string }) => apiDasIntegracoes.trocarCorrespondencia(v.id, v.registroId, v.motivo),
    meta: {
      retorno: retornoDe<{ chaveExterna: string; antes: string | null; depois: { codigo: string } | null }, { motivo: string }>((r, v) => ({
        titulo: 'Correspondência trocada',
        ...retornoDaTroca(r, v.motivo),
      })),
      silenciarFalha: true,
    },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    trocar.reset();
    setRegistro('');
    setMotivo('');
    aoFechar();
  }
  return (
    <Janela
      titulo={alvo ? `Trocar o cadastro da chave ${alvo.chaveExterna}?` : 'Trocar o cadastro?'}
      descricao={alvo ? `Hoje ela aponta ${alvo.registroCodigo ?? '—'} · ${alvo.registroNome ?? ''}. A trilha guarda o anterior e o motivo.` : ''}
      aberta={alvo !== null}
      aoFechar={fechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>Manter como está</Botao>
          <Botao nivel="primario" disabled={trocar.isPending || !registro || motivo.trim().length < 10} onClick={() => { if (alvo) trocar.mutate({ id: alvo.id, registroId: registro, motivo: motivo.trim() }); }}>
            {trocar.isPending ? 'Trocando…' : 'Trocar cadastro'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {trocar.isError ? <Recusa erro={trocar.error} /> : null}
        {alvo ? <EscolhaDoRegistro entidade={alvo.entidade} valor={registro} aoEscolher={setRegistro} /> : null}
        <CampoDoMotivo valor={motivo} aoMudar={setMotivo} maximo={400} exemplo="A chave apontava o posto antigo, que fechou" />
      </div>
    </Janela>
  );
}

/* ======================================================= a divergencia */

export function JanelaDeResolucao({ alvo, aoFechar }: { readonly alvo: Divergencia | null; readonly aoFechar: () => void }) {
  const invalidar = useInvalidar();
  const [resolucao, setResolucao] = useState<string>(RESOLUCOES[0] ?? 'corrigida_na_plataforma');
  const [justificativa, setJustificativa] = useState('');
  const resolver = useMutation({
    mutationFn: (v: { id: string; resolucao: string; justificativa: string }) => apiDasIntegracoes.resolver(v.id, v.resolucao, v.justificativa),
    meta: {
      retorno: retornoDe<{ tipo: Divergencia['tipo']; diferenca: number; resolucao: string }, { justificativa: string }>((r, v) => ({
        titulo: 'Divergência resolvida',
        ...retornoDaResolucao(r, v.justificativa),
      })),
      silenciarFalha: true,
    },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    resolver.reset();
    setJustificativa('');
    aoFechar();
  }
  return (
    <Janela
      titulo="Resolver divergência"
      descricao={alvo ? `${ROTULO_DO_TIPO_DE_DIVERGENCIA[alvo.tipo]} · ${alvoDaDivergencia(alvo)} · diferença ${diferencaEscrita(alvo.diferenca)}` : ''}
      aberta={alvo !== null}
      aoFechar={fechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>Cancelar</Botao>
          <Botao nivel="primario" disabled={resolver.isPending || justificativa.trim().length < 10} onClick={() => { if (alvo) resolver.mutate({ id: alvo.id, resolucao, justificativa: justificativa.trim() }); }}>
            {resolver.isPending ? 'Gravando…' : 'Resolver divergência'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {resolver.isError ? <Recusa erro={resolver.error} /> : null}
        {alvo?.mensagemExterno ? <p className="txt-apoio text-pretty text-tinta-secundaria">{`O sistema de fora disse: ${alvo.mensagemExterno}`}</p> : null}
        <CampoSelecao rotulo="Como foi resolvida" valor={resolucao} aoMudar={setResolucao} opcoes={RESOLUCOES.map((r) => ({ valor: r, rotulo: ROTULO_DA_RESOLUCAO[r] ?? r }))} />
        <CampoDoMotivo rotulo="Justificativa" valor={justificativa} aoMudar={setJustificativa} maximo={1000} exemplo="O ERP lançou o indireto na conta errada; corrigido lá em 08/10" />
      </div>
    </Janela>
  );
}

/* ====================================================== a tolerancia */

export function JanelaDaTolerancia({ aberta, aoFechar, atual }: { readonly aberta: boolean; readonly aoFechar: () => void; readonly atual: number }) {
  const invalidar = useInvalidar();
  const [valor, setValor] = useState('');
  const [motivo, setMotivo] = useState('');
  const mudar = useMutation({
    mutationFn: (v: { toleranciaReais: number; motivo: string }) => apiDasIntegracoes.mudarTolerancia(v.toleranciaReais, v.motivo),
    meta: { retorno: retornoDe<{ toleranciaReais: number; antes: number }, { motivo: string }>((r, v) => ({ titulo: 'Tolerância da troca alterada', ...retornoDaTolerancia(r, v.motivo) })), silenciarFalha: true },
    onSuccess: () => {
      invalidar();
      fechar();
    },
  });
  function fechar() {
    mudar.reset();
    setValor('');
    setMotivo('');
    aoFechar();
  }
  const n = interpretarNumero(valor);
  return (
    <Janela
      titulo="Mudar a tolerância da troca"
      descricao={`Diferença em reais abaixo da qual a linha ou o total do retorno não vira divergência. Hoje: ${formatarReais(atual)}${atual === 0 ? ' (fecha ao centavo)' : ''}.`}
      aberta={aberta}
      aoFechar={fechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={fechar}>Cancelar</Botao>
          <Botao nivel="primario" disabled={mudar.isPending || n === null || n < 0 || motivo.trim().length < 10} onClick={() => { if (n !== null) mudar.mutate({ toleranciaReais: n, motivo: motivo.trim() }); }}>
            {mudar.isPending ? 'Gravando…' : 'Gravar tolerância'}
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {mudar.isError ? <Recusa erro={mudar.error} /> : null}
        <CampoTexto rotulo="Tolerância (R$)" valor={valor} aoMudar={setValor} placeholder="0,05" maxLength={10} />
        <CampoDoMotivo valor={motivo} aoMudar={setMotivo} maximo={400} exemplo="O ERP arredonda por linha; a contabilidade aceita até cinco centavos" />
      </div>
    </Janela>
  );
}
